import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import ScienceBackground from '../../components/layout/ScienceBackground';
import { FaMapMarkerAlt, FaClock, FaCalendarAlt, FaChevronRight, FaFilter, FaLaptop, FaBuilding } from 'react-icons/fa';
import API_BASE_URL, { ASSET_BASE_URL } from '../../config/api';

const TimeTables = () => {
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, physical, online
  const [selectedInstitute, setSelectedInstitute] = useState(null);

  useEffect(() => {
    fetchInstitutes();
  }, []);

  const fetchInstitutes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get_institutes.php`);
      const data = await response.json();
      if (data.status === 'success') {
        setInstitutes(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch time tables. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredInstitutes = filter === 'all' 
    ? institutes 
    : institutes.filter(inst => inst.type === filter);

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${ASSET_BASE_URL}/${path.startsWith('/') ? path.slice(1) : path}`;
  };

  return (
    <div className="font-sans bg-[#f8fafc] min-h-screen text-slate-800 overflow-x-hidden selection:bg-orange-500 selection:text-white relative">
      <ScienceBackground />
      <Navbar />

      {/* Hero Section - Modern Minimalist */}
      <div className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-8"
          >
            <div className="max-w-3xl">
              <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-600 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                Class Schedules
              </span>
              <h1 className="text-5xl md:text-7xl font-black text-slate-900 leading-tight">
                Plan Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-red-500 to-rose-600">Learning</span> Journey
              </h1>
              <p className="text-xl text-slate-500 mt-6 leading-relaxed">
                Explore our comprehensive class timetables across multiple institutes. 
                Synchronize your education with our flexible physical and digital sessions.
              </p>
            </div>

            {/* Filter Pills */}
            <div className="flex bg-white p-1.5 rounded-xl shadow-xl shadow-slate-200/50 border border-slate-100 self-start md:self-auto">
              {['all', 'physical', 'online'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all capitalize ${
                    filter === type 
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg shadow-orange-200' 
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-6 pb-32 relative z-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 border-4 border-orange-100 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="mt-6 text-slate-400 font-medium animate-pulse">Synchronizing schedules...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center text-3xl mb-6">!</div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">Connection Error</h3>
            <p className="text-slate-500">{error}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredInstitutes.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <AnimatePresence mode='popLayout'>
                  {filteredInstitutes.map((institute, idx) => (
                    <motion.div
                      key={institute.id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.4, delay: idx * 0.05 }}
                      className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-200/50 border border-slate-50 flex flex-col h-full group"
                    >
                      <div className="relative w-20 h-20 bg-white border border-slate-100 rounded-2xl flex items-center justify-center mb-6 shadow-sm transition-transform duration-500 group-hover:scale-105">
                        {institute.logo_url ? (
                          <img src={getImageUrl(institute.logo_url)} alt={institute.name} className="w-14 h-14 object-contain filter drop-shadow-sm" />
                        ) : (
                          <div className="text-orange-500 font-bold text-3xl">{institute.name.charAt(0)}</div>
                        )}
                        <div className="absolute -inset-2 bg-gradient-to-r from-orange-400/20 to-red-400/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
                      </div>
                      <h2 className="text-3xl font-black text-slate-900 mb-4">{institute.name}</h2>
                      
                      <div className="space-y-4 mb-8">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-2 bg-slate-50 rounded-lg text-orange-500">
                            <FaMapMarkerAlt size={14} />
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Location</p>
                            <p className="text-slate-700 font-semibold">{institute.location}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-2 bg-slate-50 rounded-lg text-orange-500">
                            {institute.type === 'online' ? <FaLaptop size={14} /> : <FaBuilding size={14} />}
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Session Type</p>
                            <p className="text-slate-700 font-semibold capitalize">{institute.type} Class</p>
                          </div>
                        </div>
                      </div>

                      <button 
                        onClick={() => setSelectedInstitute(institute)}
                        className="w-full mt-auto py-3 bg-slate-900 hover:bg-black text-white rounded-lg font-bold transition-all flex items-center justify-center gap-2 group"
                      >
                        View Details
                        <FaChevronRight className="text-xs group-hover:translate-x-1 transition-transform" />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {filteredInstitutes.length === 0 && !loading && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                className="py-32 text-center"
              >
                <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
                  <FaFilter size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-400">No matching schedules found</h3>
                <button 
                  onClick={() => setFilter('all')}
                  className="mt-4 text-orange-600 font-bold hover:underline"
                >
                  Clear all filters
                </button>
              </motion.div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Modal */}
      <AnimatePresence>
        {selectedInstitute && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setSelectedInstitute(null)}
          >
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 50, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-4xl rounded-[24px] shadow-2xl flex flex-col max-h-[90vh] relative overflow-hidden"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-orange-500 to-red-600 p-6 sm:p-8 flex items-start justify-between relative z-10 flex-shrink-0">
                <div className="flex items-center gap-5 pr-4">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center border border-white/20 shadow-xl shadow-orange-900/10 flex-shrink-0">
                    {selectedInstitute.logo_url ? (
                      <img src={getImageUrl(selectedInstitute.logo_url)} alt={selectedInstitute.name} className="w-12 h-12 object-contain" />
                    ) : (
                      <div className="text-2xl font-black text-orange-500">{selectedInstitute.name.charAt(0)}</div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-white font-black text-2xl sm:text-3xl italic tracking-wide uppercase leading-tight">
                      {selectedInstitute.name} TIMETABLE
                    </h3>
                    <p className="text-white/90 font-bold italic mt-1 text-sm lowercase">
                      {selectedInstitute.location}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedInstitute(null)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center text-white transition-colors flex-shrink-0"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body Content */}
              <div className="p-4 sm:p-8 flex-grow flex flex-col overflow-hidden bg-white">
                <div className="border border-orange-100 rounded-[20px] overflow-hidden flex flex-col flex-grow">
                  {/* Table Column Headers */}
                  <div className="bg-[#fffcf9] px-6 sm:px-10 py-5 grid grid-cols-2 gap-4 sm:gap-6 border-b border-orange-50 flex-shrink-0">
                    <div className="text-orange-600 font-black text-[10px] sm:text-xs tracking-widest uppercase">
                      Class Segment
                    </div>
                    <div className="text-orange-600 font-black text-[10px] sm:text-xs tracking-widest uppercase text-right">
                      Window
                    </div>
                  </div>

                  {/* Table Body */}
                  <div className="overflow-y-auto custom-scrollbar flex-grow bg-white">
                    {selectedInstitute.schedules && selectedInstitute.schedules.length > 0 ? (
                      selectedInstitute.schedules.map((schedule, idx) => (
                        <motion.div 
                          key={idx}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="grid grid-cols-2 gap-4 sm:gap-6 py-6 px-6 sm:px-10 border-b border-slate-50 last:border-0 items-center hover:bg-slate-50/50 transition-colors"
                        >
                          <div>
                            <div className="text-slate-900 font-black text-sm sm:text-[15px]">
                              {schedule.class_segment}
                            </div>
                            <div className="text-orange-500 font-black text-[9px] sm:text-[10px] uppercase tracking-widest mt-1">
                              {schedule.class_day}
                            </div>
                          </div>
                          <div className="text-slate-800 font-bold text-sm sm:text-[15px] text-right">
                            {schedule.time_window}
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center py-20 h-full">
                        <p className="text-slate-400 font-medium italic text-[15px]">
                          No schedules available for this portal yet.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Button */}
                <button 
                  onClick={() => setSelectedInstitute(null)}
                  className="w-full mt-4 sm:mt-6 py-4 bg-[#f8fafc] hover:bg-slate-100 text-slate-700 text-[13px] font-black italic tracking-[0.2em] uppercase rounded-xl transition-colors flex-shrink-0"
                >
                  Close Portal
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Decorative Floating Elements */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-0 overflow-hidden opacity-40">
        <div className="absolute top-[10%] right-[-5%] w-96 h-96 bg-orange-200 rounded-full blur-[120px] mix-blend-multiply animate-pulse"></div>
        <div className="absolute bottom-[20%] left-[-5%] w-[30rem] h-[30rem] bg-rose-100 rounded-full blur-[120px] mix-blend-multiply animate-blob"></div>
      </div>

      <Footer />
    </div>
  );
};

export default TimeTables;

